// What a list shows (brief 1A-6), worked out without the page: sort, filters, stage chips,
// selection, and the choices remembered per list per person. app/ui/list.ts draws it.

import {
  compareText,
  DEFAULT_PER,
  passesFilters as passes,
  perOf,
  type ColumnFilter,
  type PerPage,
  type SortDir,
} from "../../server/rules/list-page";

export type { ColumnFilter, PerPage, SortDir };
export type View = "list" | "board";

export interface ListState {
  sort: { col: string; dir: SortDir } | null;
  filters: Record<string, ColumnFilter>;
  /** The stage chip picked above the list; null is "All". */
  chip: string | null;
  view: View;
  /** Brief 1E-32: rows a page (20, 50 or 100), kept per list and person. */
  per: PerPage;
}

export const EMPTY_STATE: ListState = {
  sort: null,
  filters: {},
  chip: null,
  view: "list",
  per: DEFAULT_PER,
};

/** What the state needs from a column: its id and the text a row shows in it. */
export interface StateColumn<T> {
  id: string;
  text: (row: T) => string;
}

/** A header click: off → ascending → descending → off. Another column starts at ascending. */
export function nextSort(sort: ListState["sort"], col: string): ListState["sort"] {
  if (!sort || sort.col !== col) return { col, dir: "ascending" };
  return sort.dir === "ascending" ? { col, dir: "descending" } : null;
}

/** Whether a row passes every column filter. */
export function passesFilters<T>(
  row: T,
  columns: readonly StateColumn<T>[],
  filters: ListState["filters"],
): boolean {
  return passes(row, columns, filters);
}

/** Rows that pass the column filters, before the stage chip (the chips count these). */
export function filteredRows<T>(
  rows: readonly T[],
  columns: readonly StateColumn<T>[],
  state: ListState,
): T[] {
  return rows.filter((r) => passesFilters(r, columns, state.filters));
}

/** The rows the list shows: filtered, narrowed to the picked chip, then sorted. */
export function visibleRows<T>(
  rows: readonly T[],
  columns: readonly StateColumn<T>[],
  state: ListState,
  chipOf?: (row: T) => string,
): T[] {
  let out = filteredRows(rows, columns, state);
  if (state.chip !== null && chipOf) out = out.filter((r) => chipOf(r) === state.chip);
  const sort = state.sort;
  const col = sort && columns.find((c) => c.id === sort.col);
  if (sort && col) {
    const dir = sort.dir === "ascending" ? 1 : -1;
    out = out
      .map((r, i) => ({ r, i }))
      .sort((a, b) => dir * compareText(col.text(a.r), col.text(b.r)) || a.i - b.i)
      .map((x) => x.r);
  }
  return out;
}

/** The chips over the list: All with its count, then each value that has rows, in order. */
export function chipCounts<T>(
  rows: readonly T[],
  values: readonly string[],
  chipOf: (row: T) => string,
): { all: number; chips: { value: string; count: number }[] } {
  return {
    all: rows.length,
    chips: values
      .map((value) => ({ value, count: rows.filter((r) => chipOf(r) === value).length }))
      .filter((c) => c.count > 0),
  };
}

/** Select all picks every visible row; when they're all picked already, it clears them. */
export function toggleAll(
  selected: ReadonlySet<string>,
  visibleIds: readonly string[],
): Set<string> {
  const all = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  return all ? new Set() : new Set(visibleIds);
}

/** Only rows still on screen stay selected, so the bulk bar counts what you can see. */
export function keepVisible(
  selected: ReadonlySet<string>,
  visibleIds: readonly string[],
): Set<string> {
  return new Set(visibleIds.filter((id) => selected.has(id)));
}

/** Whether any column filter is set. */
export const hasFilters = (state: ListState) =>
  Object.values(state.filters).some((f) => !!f.pick || !!f.text?.trim());

// ---- Remembered per list, per person, in this browser ----------------------------------------

/** Where a list's choices are kept: one key per person and list. */
export const stateKey = (userId: string, listId: string) => `fc-list:${userId}:${listId}`;

interface Store {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The choices this person made on this list, or the defaults (List view, nothing sorted). */
export function loadState(store: Store | null, userId: string, listId: string): ListState {
  try {
    const raw = store?.getItem(stateKey(userId, listId));
    if (!raw) return { ...EMPTY_STATE };
    const s = JSON.parse(raw) as Partial<ListState>;
    const sort =
      s.sort && typeof s.sort.col === "string" && ["ascending", "descending"].includes(s.sort.dir)
        ? s.sort
        : null;
    return {
      sort,
      filters: s.filters && typeof s.filters === "object" ? s.filters : {},
      chip: typeof s.chip === "string" ? s.chip : null,
      view: s.view === "board" ? "board" : "list",
      per: perOf(s.per),
    };
  } catch {
    return { ...EMPTY_STATE };
  }
}

export function saveState(store: Store | null, userId: string, listId: string, state: ListState) {
  try {
    store?.setItem(stateKey(userId, listId), JSON.stringify(state));
  } catch {
    // Private windows may refuse storage; the list still works for this page.
  }
}
